import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  PaymentSchema,
  PaymentCreateInputSchema,
  PaymentUpdateInputSchema,
  type Payment,
  type PaymentCreateInput,
  type PaymentUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `payment` (mouvement d'argent, org-wide en v0).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`PaymentCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset) ; l'accès fin par rôle
 * (vente, caisse, facturation) viendra après. Aucune donnée de carte en clair
 * n'entre ici : seuls pspToken et last4 (cf. @taki/data-model).
 */

export type { PaymentCreateInput };

/** Nom de la collection Firestore de la famille payment. */
export const PAYMENTS_COLLECTION = 'payments';

/**
 * Repository typé pour la famille `payment`.
 * Toute lecture est validée par `PaymentSchema`, toute écriture par
 * `PaymentCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface PaymentRepository {
  create(input: PaymentCreateInput): Promise<Payment>;
  update(id: string, patch: PaymentUpdateInput): Promise<Payment>;
  list(options?: ListOptions): Promise<Payment[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Payment | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Payment>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Payment>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Payment>;
}

const paymentConverter = createConverter<Payment>((raw: DocumentData) => PaymentSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createPaymentRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): PaymentRepository {
  const col = collection(db, PAYMENTS_COLLECTION).withConverter(paymentConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Payment>(db, col, PAYMENTS_COLLECTION, getActorId);

  return {
    async create(input: PaymentCreateInput): Promise<Payment> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = PaymentCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.method !== undefined) optional['method'] = fields.method;
      if (fields.direction !== undefined) optional['direction'] = fields.direction;
      if (fields.paidAt !== undefined) optional['paidAt'] = fields.paidAt;
      if (fields.invoiceIds !== undefined) optional['invoiceIds'] = fields.invoiceIds;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.voucherId !== undefined) optional['voucherId'] = fields.voucherId;
      if (fields.creditId !== undefined) optional['creditId'] = fields.creditId;
      if (fields.subscriptionId !== undefined) optional['subscriptionId'] = fields.subscriptionId;
      if (fields.refundOfPaymentId !== undefined) optional['refundOfPaymentId'] = fields.refundOfPaymentId;
      if (fields.pspToken !== undefined) optional['pspToken'] = fields.pspToken;
      if (fields.last4 !== undefined) optional['last4'] = fields.last4;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
      if (fields.couleur != null) optional['couleur'] = fields.couleur;
      if (fields.photo != null) optional['photo'] = fields.photo;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        amount: fields.amount,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Payment);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return PaymentSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: PaymentUpdateInput): Promise<Payment> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = PaymentUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, PAYMENTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec PaymentSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`payment ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Payment[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Payment | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
